/**
 * @copyright Copyright © 2020-2025 Airbus DS SLC. All rights reserved.
 *
 * @module Bootstrap
 * @description This module provides a Bootstrap class for managing module dependencies and initialization in a web application.
 * @example
 */

import {Tarjan} from "@airbus/library-tarjan";

/**
 * @typedef {() => void | () => Promise<void>} ModuleInit
 */

/**
 * @typedef {Object} BootstrapOptions
 * @property {import("loglevel").RootLogger} log - Log manager.
 */

/**
 * Module name validation pattern
 */
const MODULE_NAME_PATTERN = /^[a-z][a-z-]+$/;

const DEBUG_PERF = false;

/**
 * The module object containing name, dependencies, and initialization function.
 * @typedef {Object} ModuleDescription
 * @property {string} name - The name of the module.
 * @property {string[]} deps - The dependencies of the module.
 * @property {ModuleInit} init - The initialization function of the module.
 */

/**
 * @class Bootstrap
 * @classdesc Manages module registration, dependency resolution, and initialization.
 */
export class Bootstrap {
    /**
     * @type {import("loglevel").Logger}
     */
    #log;
    #tarjan;
    #modules;
    #waiters;
    #prewaiters;
    #T0;

    /**
     * Creates an instance of Bootstrap.
     * @param {BootstrapOptions} options - The option object containing logging utilities.
     */
    constructor(options) {
        this.#T0 = performance.now();
        this.#log = options.log.getLogger("airbus-bootstrap");
        this.#log.setLevel(this.#log.levels.WARN);
        this.#log.info("Bootstrap initialized");
        this.#tarjan = new Tarjan();
        this.#modules = {};
        this.#waiters = {};
        this.#prewaiters = {};
        this.moduleInit = this.moduleInit.bind(this);
        this.onProgress = null;
    }

    /**
     * Registers a module with its dependencies.
     * @param {ModuleDescription} description - Description of the module.
     */
    register_module({name, deps, init}) {
        this.#log.debug(`Registering module: ${name}`);
        if (!MODULE_NAME_PATTERN.test(name)) {
            this.#log.warn(`Module name "${name}" has an incorrect pattern (${MODULE_NAME_PATTERN})`);
        }
        for (let depName of deps) {
            this.#tarjan.graph.connectVertex(name, depName);
        }
        this.#modules[name] = {name, deps, init};
        this.#log.debug(`Module ${name} registered with ${deps.length} dependencies`);
    }

    /**
     * Initializes a module and logs performance metrics.
     * @param {string} name - The name of the module to initialize.
     * @returns {Promise<void>} A promise that resolves when the module is initialized.
     */
    async moduleInit(name) {
        this.#log.debug(`Starting initialization of module: ${name}`);
        if (name in this.#waiters) {
            this.#log.debug(`Module ${name} already in initialization, returning existing promise`);
            return this.#waiters[name];
        }

        const t0 = performance.now() - this.#T0;
        DEBUG_PERF && this.#log.debug("start waiting", name, "at t0 =", t0);
        this.#waiters[name] = this.#modules[name].init();
        await this.#waiters[name];
        const t1 = performance.now() - this.#T0;
        const dt = t1 - t0;
        DEBUG_PERF && this.#log.debug("stop waiting", name, "at t1 =", t1, "dt =", dt);

        const TMAXL = 500;
        const TMAXH = 1000;
        if (dt > TMAXH) {
            this.#log.error(`Module ${name} is significantly slowing down application loading performance: ${Math.round(dt)}ms > ${TMAXH}ms (max)`);
        } else if (dt > TMAXL) {
            this.#log.warn(`Module ${name} initialization took longer than expected: ${Math.round(dt)}ms > ${TMAXL}ms (max)`);
        }

        if (name in this.#prewaiters) {
            this.#prewaiters[name].resolve();
        }

        this.onProgress?.(new Set(Object.keys(this.#waiters)));

        this.#log.info(`Module ${name} initialized successfully`);
        return this.#waiters[name];
    }

    /**
     * Computes the transitive dependencies of a module using BFS on the Tarjan graph.
     * @param {string} name - The name of the module.
     * @returns {Set<string>} The set of transitive dependencies including the module itself.
     */
    getTransitiveDeps(name) {
        const visited = new Set();
        const queue = [name];
        while (queue.length > 0) {
            const current = queue.shift();
            if (visited.has(current)) {
                continue;
            }
            visited.add(current);
            const vertex = this.#tarjan.graph.vertices_by_name[current];
            if (vertex) {
                for (const connection of vertex.connections) {
                    queue.push(connection.name);
                }
            }
        }
        return visited;
    }

    /**
     * Waits for multiple modules to be initialized.
     * @param {string[]} names - The names of the modules to wait for.
     * @returns {Promise<void[]>} A promise that resolves when all specified modules are initialized.
     */
    async wait(names) {
        this.#log.debug(`Waiting for modules: ${names.join(", ")}`);
        return Promise.all(
            names.map((name) => {
                if (name in this.#waiters) {
                    this.#log.debug(`Module ${name} already initialized, using existing promise`);
                    return this.#waiters[name];
                }
                if (name in this.#prewaiters) {
                    this.#log.debug(`Module ${name} already in prewait state, using existing promise`);
                    return this.#prewaiters[name].promise;
                }
                const prewaiter = {};
                prewaiter.promise = new Promise((resolve) => {
                    prewaiter.resolve = resolve;
                });
                this.#prewaiters[name] = prewaiter;
                this.#log.debug(`Added prewaiter for module ${name}`);
                return this.#prewaiters[name].promise;
            })
        );
    }

    /**
     * Boots the application by initializing all modules in the correct order.
     * @returns {Promise<void>} A promise that resolves when all modules are initialized.
     */
    async boot() {
        this.#log.info("Starting application boot process");
        this.#T0 = performance.now();
        const componentsGroups = this.#tarjan.run();
        let componentsPrev = new Set();
        let componentsNext = new Set();

        const preload = new Set(
            Object.values(this.#modules)
                .filter((module) => module.deps.length === 0)
                .map((module) => module.name)
        );
        this.#log.debug(`Preloading modules: ${[...preload].join(", ")}`);
        for (const module of preload) {
            this.moduleInit(module);
        }

        const deps = new Set(Object.values(this.#modules).flatMap((module) => module.deps));
        const postload = Object.values(this.#modules)
            .filter((module) => !deps.has(module.name))
            .map((module) => module.name);

        for (const componentsGroup of componentsGroups) {
            for (const component of componentsGroup) {
                if (!component.connections.every(({name}) => componentsPrev.has(name))) {
                    this.#log.debug(`Loading modules: ${[...componentsNext].join(", ")}`);
                    for (const name of componentsNext) {
                        if (!(name in this.#modules)) {
                            this.#log.error(`Module ${name} not found`);
                            throw new Error(`module ${name} not found`);
                        }
                    }
                    await Promise.all([...componentsNext].map(this.moduleInit));
                    componentsPrev = new Set([...componentsPrev, ...componentsNext]);
                    componentsNext = new Set();
                }
                if (!postload.includes(component.name)) {
                    componentsNext.add(component.name);
                }
            }
        }

        this.#log.debug(`Loading remaining modules: ${[...componentsNext].join(", ")}`);
        await Promise.all([...componentsNext].map(this.moduleInit));

        this.#log.debug(`Postloading modules: ${[...postload].join(", ")}`);
        for (const module of postload) {
            this.moduleInit(module);
        }

        this.#log.info("Application boot process completed successfully");
    }
}
